import { getApiErrorCode, getCodedErrorMessage } from "../../utils/apiError";

// Words for the variant pages: matrix rows, questions named by their number in the variant, check issues and the
// errors of the writes.

const ROMAN = ["", "I", "II", "III", "IV"];
export const LEVELS = ["A", "B", "C"];

export const quarterLabel = (t, quarter) =>
  quarter ? t("variants.row.quarter", { quarter: ROMAN[quarter] || quarter }) : t("variants.row.no_quarter");
export const weekLabel = (t, week) => (week ? t("variants.row.week", { week }) : t("variants.row.no_week"));

// A matrix row as the variant pages get it (a row_ref): {uuid, grade, topic | null, subtopics, quarter, week, hours};
// a pre-v2 server sends {topic, subtopic} without a uuid.

// The subtopics a row plans; none for a topic planned whole.
export const rowSubtopics = (row) => (Array.isArray(row?.subtopics) ? row.subtopics : row?.subtopic ? [row.subtopic] : []);

// A week whose topic the matrix has not chosen yet: its places can't be filled (no question has it).
export const hasNoTopic = (row) => !!row && !row.topic;

const SUBTOPICS_SHOWN = 2;

// "Topic › Subtopic, Subtopic" (more than two: the first two and "+N"); a topic planned whole is its name, and a row
// without a topic says so.
export const rowTopic = (t, row) => {
  if (hasNoTopic(row)) return t("variants.row.no_topic");
  const names = rowSubtopics(row).map((subtopic) => subtopic?.name).filter(Boolean);
  const more = names.length > SUBTOPICS_SHOWN ? ` +${names.length - SUBTOPICS_SHOWN}` : "";
  const subtopics = names.length ? `${names.slice(0, SUBTOPICS_SHOWN).join(", ")}${more}` : "";
  return [row?.topic?.name, subtopics].filter(Boolean).join(" › ");
};

// "II тоқсан, 3-апта": when a row is planned.
export const rowWhen = (t, row) =>
  [row?.quarter ? quarterLabel(t, row.quarter) : null, row?.week ? weekLabel(t, row.week) : null].filter(Boolean).join(", ");

// "II тоқсан, 3-апта, Topic › Subtopic": where a matrix row is.
export const rowPlace = (t, row) => [rowWhen(t, row), rowTopic(t, row)].filter(Boolean).join(", ");

// A row's key in one read of the check: its uuid, which changes with every save of the matrix (a pre-v2 server's
// rows have none: by topic and subtopic).
export const rowKey = (row) => row?.uuid || `${row?.topic?.uuid || ""}|${row?.subtopic?.uuid || ""}`;

// What a row is about, kept over saves of the matrix: its topic and subtopics, which no other row of the folder has.
// A row without a topic has none.
const rowContent = (row) =>
  row?.topic?.uuid
    ? `${row.topic.uuid}|${rowSubtopics(row)
        .map((subtopic) => subtopic?.uuid)
        .sort()
        .join(",")}`
    : null;

// A row's key for the page's own elements, kept over saves of the matrix (a row without a topic: its uuid), so a
// save that leaves a row as it was does not lay it out anew (and take the focus from its buttons).
export const rowIdentity = (row) => rowContent(row) || rowKey(row);

// The week a row plans (grade, quarter and week): the matrix may have several rows in one.
export const weekKey = (row) => `${row?.grade ?? ""}|${row?.quarter ?? ""}|${row?.week ?? ""}`;

// A place of the composition in one read: "<rowKey>:<level>:<index>" (the index-th place of that level in the row).
export const placeKey = (row, level, index) => `${rowKey(row)}:${level}:${index}`;

// The entry of `row` in another read of the check (`entries`: check.rows): by its key, else, after a save of the
// matrix, by its topic and subtopics, else (its subtopics changed) the week's one row of that topic. null when it
// is gone.
export function findRow(entries, row) {
  const list = entries || [];
  const key = rowKey(row);
  const content = rowContent(row);
  const found = list.find((entry) => rowKey(entry.row) === key) || (content && list.find((entry) => rowContent(entry.row) === content));
  if (found || !content) return found || null;
  const week = list.filter((entry) => entry.row?.topic?.uuid === row.topic.uuid && weekKey(entry.row) === weekKey(row));
  return week.length === 1 ? week[0] : null;
}

// The bank's questions of a row and difficulty (the bank opens in a new tab, the variant stays): the subject with
// the topic, its subtopic when the row plans only one (the bank filters by one), their names for the bank's pickers,
// and the difficulty as filters. A row without a topic has nothing to look for there: null.
export const bankHref = (folderUuid, row, level) => {
  if (!row?.topic?.uuid) return null;
  const params = new URLSearchParams({ folder: folderUuid, topic: row.topic.uuid, topic_name: row.topic.name || "" });
  const subtopics = rowSubtopics(row);
  if (subtopics.length === 1 && subtopics[0]?.uuid) {
    params.set("subtopic", subtopics[0].uuid);
    params.set("subtopic_name", subtopics[0].name || "");
  }
  if (level) params.set("difficulty", level);
  return `/questions?${params.toString()}`;
};

// A question of the variant the user no longer reads (moved since to a folder not given to them): the server sends
// only {uuid, code, passage: {uuid} | null, readable: false}, without its text, options, difficulty or status.
export const isUnreadable = (question) => question?.readable === false;

// {question uuid: {number, code, tq}} of the variant's rows, for naming questions by their number in it. `extra`
// (questions not in the variant, e.g. candidates) are named by their code: {number: null, code}.
export function questionRefs(testQuestions, extra = []) {
  const refs = new Map();
  extra.forEach((question) => {
    if (question?.uuid) refs.set(question.uuid, { number: null, code: question.code || "" });
  });
  (testQuestions || []).forEach((tq, index) => {
    if (tq?.question?.uuid) refs.set(tq.question.uuid, { number: tq.order ?? index + 1, code: tq.question.code || "", tq });
  });
  return refs;
}

const refText = (t, ref) => {
  if (ref.number == null) return ref.code;
  return ref.code ? t("variants.question_ref", { number: ref.number, code: ref.code }) : t("variants.number", { number: ref.number });
};

// "№14 (QA41-0057), №20 (QA41-0058)", in the variant's order; questions it does not know are counted.
export function questionList(t, uuids, refs) {
  const known = [];
  let unknown = 0;
  (uuids || []).forEach((uuid) => {
    const ref = refs.get(uuid);
    if (ref && (ref.number != null || ref.code)) known.push(ref);
    else unknown += 1;
  });
  known.sort((a, b) => (a.number ?? Infinity) - (b.number ?? Infinity));
  const parts = known.map((ref) => refText(t, ref));
  if (unknown) parts.push(t("variants.questions_not_in_variant", { count: unknown }));
  return parts.join(", ");
}

// The codes of the variant's questions of a passage, in the variant's order: ["QA41-0060", "QA41-0061"].
export const passageCodes = (testQuestions, passageUuid) =>
  (testQuestions || []).filter((tq) => passageUuid && tq?.question?.passage?.uuid === passageUuid).map((tq) => tq.question.code || "—");

// Words that name a passage by its questions' codes: `<key>_code` for one ({{code}}), `<key>_codes` for several
// ({{codes}}, {{number}}). Chosen by the number of codes, not by plural rules: Russian ones put 21 with 1.
export const byCodes = (t, key, codes) =>
  codes.length === 1 ? t(`${key}_code`, { code: codes[0] }) : t(`${key}_codes`, { codes: codes.join(", "), number: codes.length });

// A passage without a name (one of a question the user no longer reads comes as its uuid alone), named by the codes
// of its questions in the variant: "QA41-0060, QA41-0061 сұрақтарының мәтіні".
const passageOf = (t, codes) => byCodes(t, "variants.passage_by", codes);

// The passages of the variant's questions: {uuid: name}, or the codes of their questions for those without one.
export function passageNames(t, testQuestions) {
  const names = new Map();
  (testQuestions || []).forEach((tq) => {
    const passage = tq?.question?.passage;
    if (passage?.uuid && !names.has(passage.uuid)) {
      names.set(passage.uuid, passage.name?.trim() || passageOf(t, passageCodes(testQuestions, passage.uuid)));
    }
  });
  return names;
}

// "A10 B6 C4, 20 in total, 100 points": a {A, B, C, count, points} total in words (the points with their own plural).
export const totalsText = (t, total) =>
  t("variants.totals_text", {
    A: total?.A ?? 0,
    B: total?.B ?? 0,
    C: total?.C ?? 0,
    count: total?.count ?? 0,
    pointsText: t("difficulty.points", { count: total?.points ?? 0 }),
  });

// The blocking issues of a check as it lists them: an empty variant is short in every place, which one line says
// instead of one per place, and a week without a topic is one line for all its levels and rows (row_without_topic
// comes per planned level; its line names the week, and its places wait for a topic, not for questions). `count`
// is the number of lines (the header's "N blocking issues").
export function blockingLines(check) {
  const topicless = new Set();
  const blocking = (check?.issues || []).filter((issue) => {
    if (!issue.blocking || (issue.code === "row_short" && hasNoTopic(issue.row))) return false;
    if (issue.code !== "row_without_topic") return true;
    const key = weekKey(issue.row);
    if (topicless.has(key)) return false;
    topicless.add(key);
    return true;
  });
  const emptyShort = (check?.actual?.count ?? 0) === 0 && blocking.some((issue) => issue.code === "row_short");
  const issues = emptyShort ? blocking.filter((issue) => issue.code !== "row_short") : blocking;
  return { emptyShort, issues, count: issues.length + (emptyShort ? 1 : 0) };
}

/**
 * One issue of the check in plain words. `refs` names the questions by their number in the variant, `passages`
 * the passages by name. An unknown code says its code.
 */
export function issueText(t, issue, { refs, passages }) {
  const list = questionList(t, issue.questions, refs);
  const count = issue.questions?.length || 0;
  switch (issue.code) {
    case "row_short":
    case "row_extra":
      return t(`variants.issues.${issue.code}`, {
        place: rowPlace(t, issue.row),
        level: issue.level,
        planned: issue.planned ?? 0,
        actual: issue.actual ?? 0,
        count: Math.abs((issue.planned ?? 0) - (issue.actual ?? 0)),
      });
    // Named by its week: its topic is what is missing.
    case "row_without_topic":
      return t("variants.issues.row_without_topic", { place: rowWhen(t, issue.row) || t("variants.row.no_week") });
    case "passage_incomplete": {
      const names = (issue.passages || []).map((uuid) => passages.get(uuid)).filter(Boolean);
      return t("variants.issues.passage_incomplete", { names: names.join(", ") || "—", count: issue.passages?.length || 0 });
    }
    case "not_standard":
      return t("variants.issues.not_standard", { planned: totalsText(t, issue.planned) });
    case "no_matrix":
    case "plan_changed":
      return t(`variants.issues.${issue.code}`);
    default:
      return t(`variants.issues.${issue.code}`, {
        list,
        count,
        defaultValue: t("variants.issues.unknown", { code: issue.code, list: list || "—" }),
      });
  }
}

/**
 * The message of a failed variant write or read: the variant's own words for its codes (variants.errors.<code>,
 * then errors.<code>), with the questions it names by their number in the variant; `fallbackKey` otherwise.
 */
export function variantErrorMessage(t, err, { refs = new Map(), fallbackKey = "variants.errors.failed" } = {}) {
  const response = err?.response;
  if (!response) return t("variants.errors.network");
  const data = response.data && typeof response.data === "object" ? response.data : {};
  const code = getApiErrorCode(err);
  if (response.status === 403 && (!code || code === "permission_denied")) return t("variants.errors.forbidden");
  if (response.status === 404 && (!code || code === "not_found")) return t("variants.errors.not_found");
  const generic = getCodedErrorMessage(t, err, t(fallbackKey));
  let message = code
    ? t(`variants.errors.${code}`, {
        ...data,
        names: (data.booklets || []).map((b) => b?.name).filter(Boolean).join(", "),
        level: data.level || "",
        place: data.row ? rowPlace(t, data.row) : "",
        defaultValue: generic,
      })
    : generic;
  if (Array.isArray(data.questions) && data.questions.length) {
    message = `${message} ${t("variants.errors.questions", { list: questionList(t, data.questions, refs), count: data.questions.length })}`;
  }
  return message;
}

// A 403 / 404 of the variant itself: it is not there for this account.
export const isOutOfReach = (err) => [403, 404].includes(err?.response?.status);
